import type { JSX } from "react";

import dayjs from "dayjs";
import mapValues from "lodash/mapValues";

import { shortMonthAndYear } from "@similarweb/pro-logic-components";

import type { IChosenItem } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { FORMAT_VISUAL } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import {
    DomainPositionCell,
    DomainPositionCellHeader,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/DomainPositionCell";
import { PositionAbsoluteChangeCell } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/PositionAbsoluteChangeCell";
import { PositionChangeCell } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/PositionChangeCell";
import { getCustomContainerForTopUrlsTooltip } from "app/pages/keyword-research/website-analysis/pages/website-keywords/WebsiteKeywordsPageUtillities2";
import {
    DefaultCellRightAlign,
    IndexCell,
    SearchKeywordCellPopUpDropDown,
    UrlCellWebsiteAnalysis,
} from "app/components/React/Table/cells";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import {
    DefaultCellHeader,
    DefaultCellHeaderRightAlign,
    HeaderCellBlank,
} from "app/components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { GoToText, TxtWrapper } from "app/components/React/Table/Utils/quickNavStyledComponents";
import { translate } from "app/filters/i18nFilter";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { SerpTableCellCompare } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellCompare";
import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { dateForTooltip } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import type { ISortDirection } from "app/@types/userdata";
import { invertSortDirection } from "app/UtilitiesAndConstants/UtilityFunctions/sort";
import { allTrackers } from "app/services/track/track";

const FORMAT_DATE = "YYYY.MM";

interface IColumnsConfigArguments {
    domains: IChosenItem[];
    hasKwaPermission: boolean;
    sortedColumn: string;
    sortDirection: ISortDirection;
    currentMonth: string;
    duration: string;
    isWindow: boolean;
}

const href = proNavigator.href.bind(proNavigator);
const quickNavItems = () => {
    return [
        <TxtWrapper key="quick.nav.dropdown.title">
            <GoToText>quick.nav.dropdown.title</GoToText>
        </TxtWrapper>,
        {
            name: "Organic Keyword Analysis",
            id: "organicsearch_keywordAnalysis_2",
            iconName: "search-keywords",
            iconSize: "sm",
            text: translate("quick.nav.dropdown.organic.keyword.analysis"),
            sourceType: EKeywordTabs[EKeywordTabs.Organic],
        },
        {
            name: "PPC Keyword Analysis",
            id: "organicsearch_keywordAnalysis_2",
            iconName: "serp-paid",
            iconSize: "sm",
            text: translate("quick.nav.dropdown.ppc.keyword.analysis"),
            sourceType: EKeywordTabs[EKeywordTabs.Paid],
        },
        {
            name: "Keyword Generator",
            id: "digitalsuite_findkeywords_keywordGeneratorTool",
            iconSize: "sm",
            iconName: "keyword-generator-grey",
            text: translate("quick.nav.dropdown.keyword.generator"),
        },
        <div
            key={"lineSeparator"}
            className={`DropdownItem-separator`}
        />,
        {
            name: "googleKeyword",
            iconName: "google-search",
            iconSize: "sm",
            text: translate("quick.nav.dropdown.keyword.in.google"),
        },
    ];
};

const onNavItemClick = (item: Exclude<ReturnType<typeof quickNavItems>[number], JSX.Element>, value: string) => {
    if (item.name === "googleKeyword") {
        allTrackers.dispatchTrackEvent("External Link", "open", `Quick Nav/${value || ""}/Google SERP`);
        window.open(`https://google.com/search?q=${value}`, "_blank");
    } else {
        const { isWWW, country, duration } = proNavigator.getParams();
        const keywordsAnalysisDestUrl = href(item.id, {
            isWWW,
            country,
            duration,
            keyword: value,
            webSource: "Total",
            selectedPageTab: item.sourceType,
        });
        allTrackers.dispatchTrackEvent("Internal Link", "open", `Quick Nav/${item.name || ""}/${value || ""}`);
        window.open(keywordsAnalysisDestUrl, "_self");
    }
};
export const getColumnsConfig = ({
    domains,
    hasKwaPermission,
    sortedColumn,
    sortDirection,
    currentMonth,
    duration,
    isWindow,
}: IColumnsConfigArguments) => {
    const durationObject = DurationService.getDurationData(duration);
    const durationTooltip = mapValues(durationObject.forTooltip as dateForTooltip, function (v) {
        return decodeURIComponent(v);
    });
    const durationTooltipParams = {
        ["currentMonth"]: durationTooltip.to,
        ["lastMonth"]: durationTooltip.from,
    };
    const compare = domains.length > 1;
    const COLUMNS_I18N_KEY = "analysis.source.search.all.table.columns";
    const { to: durationToData } = DurationService.getDurationData(duration).raw;
    const latMonthInShortMonthAndYear = durationToData.subtract(1, "month").format(shortMonthAndYear);
    const compareColumns = domains.map((domain, index) => ({
        field: `position${index}`,
        trackingName: `position${index}`,
        displayName: domain.url,
        cellComponent: DomainPositionCell(domain.url, latMonthInShortMonthAndYear),
        headerComponent: DomainPositionCellHeader(domain.url),
        tooltip: translate("ranking.distribution.columns.change.tooltip.compare"),
        sortable: true,
        sortDirection: "asc",
        width: 117,
        showTotalCount: true,
    }));
    const columns = [
        hasKwaPermission
            ? {
                  fixed: true,
                  cellComponent: RowSelectionConsumer,
                  sortable: false,
                  headerComponent: SelectAllRowsHeaderCellConsumer,
                  isResizable: false,
                  width: 40,
                  visible: true,
              }
            : false,
        {
            fixed: true,
            cellComponent: IndexCell,
            headerComponent: HeaderCellBlank,
            disableHeaderCellHover: true,
            sortable: false,
            isResizable: false,
            width: 58,
        },
        {
            field: "Keyword",
            trackingName: "Search Term",
            displayName: translate("ranking.distribution.columns.keyword"),
            tooltip: translate("analysis.source.search.all.table.columns.searchterms.title.tooltip"),
            quickNavItems: quickNavItems(),
            onNavItemClick: onNavItemClick,
            cellComponent: (props) => {
                return (
                    <SearchKeywordCellPopUpDropDown
                        {...props}
                        adsUrl={props.row.adsUrl}
                    />
                );
            },
            fixed: true,
            sortable: true,
            width: 160,
            showTotalCount: true,
        },
        ...(compare
            ? compareColumns
            : [
                  {
                      field: "position0",
                      trackingName: "Position",
                      displayName: translate("ranking.distribution.columns.position"),
                      headerComponent: DefaultCellHeader,
                      tooltip: translate("ranking.distribution.columns.position.tooltip", {
                          current: dayjs.utc(currentMonth, FORMAT_DATE).format(FORMAT_VISUAL),
                      }),
                      cellComponent: PositionChangeCell,
                      sortable: true,
                      sortDirection: "asc",
                      width: 92,
                  },
                  {
                      field: "PositionChange",
                      trackingName: "PositionChange",
                      displayName: translate("ranking.distribution.columns.change"),
                      headerComponent: DefaultCellHeaderRightAlign,
                      tooltip: translate("ranking.distribution.columns.change.tooltip", {
                          current: dayjs.utc(currentMonth, FORMAT_DATE).format(FORMAT_VISUAL),
                          previous: dayjs.utc(currentMonth, FORMAT_DATE).subtract(1, "month").format(FORMAT_VISUAL),
                      }),

                      cellComponent: PositionAbsoluteChangeCell,
                      sortable: true,
                      minWidth: 95,
                  },
              ]),
        ...(!isWindow
            ? [
                  {
                      field: "KwVolume",
                      trackingName: "KwVolume",
                      displayName: translate("website.keywords.2.0.table.monthly.volume.column"),
                      tooltip: translate("website.keywords.2.0.table.monthly.volume.column.tooltip"),
                      format: "minAbbrNumber:50",
                      headerComponent: DefaultCellHeaderRightAlign,
                      cellComponent: DefaultCellRightAlign,
                      sortable: true,
                      minWidth: 90,
                  },
              ]
            : []),
        ...(isWindow
            ? [
                  {
                      field: "KwWindowVolume",
                      trackingName: "WindowVolume",
                      displayName: translate("website.keywords.2.0.table.window.volume.column"),
                      tooltip: translate("website.keywords.2.0.table.window.volume.column.tooltip"),
                      format: "minAbbrNumber:50",
                      headerComponent: DefaultCellHeaderRightAlign,
                      cellComponent: DefaultCellRightAlign,
                      sortable: true,
                      minWidth: 94,
                  },
                  {
                      field: "VolumeChange",
                      trackingName: "VolumeChange",
                      displayName: translate("website.keywords.2.0.table.window.volume.change.column"),
                      tooltip: translate("website.keywords.2.0.table.volume.change.column.tooltip"),
                      cellComponent: (props) => {
                          const { change } = props.value;
                          const absChange = Math.abs(change);
                          const precision = absChange > 0 && absChange < 0.1 ? 1 : 0;
                          return (
                              <ChangeStateCell
                                  {...props}
                                  undefinedSign={"-"}
                                  isPercentage={true}
                                  cellFilter={`simplePercentage:${precision}`}
                              />
                          );
                      },
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "percentagesign",
                      sortable: false,
                      width: 104,
                  },
              ]
            : []),
        {
            field: "KwVolumeAverage",
            trackingName: "AverageVolume",
            displayName: translate("analysis.source.search.all.table.columns.average.volume.title"),
            tooltip: translate("website.keywords.2.0.table.average.volume.column.tooltip"),
            format: "minAbbrNumber:50",
            headerComponent: DefaultCellHeaderRightAlign,
            cellComponent: DefaultCellRightAlign,
            sortable: false,
            minWidth: 92,
        },
        {
            field: "Cpc",
            trackingName: "CPC",
            displayName: translate(`${COLUMNS_I18N_KEY}.cpc.title`),
            tooltip: translate(`${COLUMNS_I18N_KEY}.cpc.title.tooltip`),
            headerComponent: DefaultCellHeaderRightAlign,
            cellComponent: DefaultCellRightAlign,
            format: "CPC",
            sortDirection: "desc",
            sortable: true,
            minWidth: 90,
        },
        {
            field: "Serp",
            trackingName: compare ? "Total Group Traffic Share" : "Traffic Share",
            displayName: translate("serp.column.name"),
            tooltip: translate("serp.column.tooltip"),
            headerComponent: (props) => {
                return <DefaultCellHeader {...props} />;
            },
            cellComponent: compare ? SerpTableCellCompare : SerpTableCellSingle,
            width: compare ? 210 : 133,
            sortable: false,
        },
        ...(compare
            ? [
                  {
                      field: "TopURLs",
                      displayName: translate("keywords.by.website.table.columns.top.url.title"),
                      tooltip: `website.keywords.2.0.table.column.tooltip.destination.Desktop.title`,
                      cellComponent: (props) => (
                          <UrlCellWebsiteAnalysis
                              value={props.value}
                              customContainer={getCustomContainerForTopUrlsTooltip(props.value, props.row.Keyword)}
                          />
                      ),
                      cellClass: "url-cell",
                      sortable: false,
                      minWidth: 210,
                  },
              ]
            : [
                  {
                      field: "TopUrl",
                      displayName: translate("keywords.by.website.table.columns.top.url.title"),
                      tooltip: `website.keywords.2.0.table.column.tooltip.destination.Desktop.title`,
                      cellComponent: UrlCellWebsiteAnalysis,
                      cellClass: "url-cell",
                      sortable: false,
                      minWidth: 210,
                  },
              ]),
    ].filter((column) => !!column);

    return columns.map<any>((col: any) => {
        const isSorted = sortedColumn && col.field === sortedColumn;
        return {
            ...col,
            visible: true,
            sortable: col.sortable || false,
            headerComponent: col.headerComponent || DefaultCellHeader,

            isSorted,
            sortDirection: isSorted ? (col.inverted ? invertSortDirection(sortDirection) : sortDirection) : "desc",
        };
    });
};
